import { createBrowserRouter } from 'react-router';

import { AppLayout, NotFound, PublicLayout } from './components/Layouts.js';
import { Account } from './pages/Account.js';
import { Area } from './pages/Area.js';
import { Changes } from './pages/Changes.js';
import { Modernization } from './pages/Modernization.js';
import { ModernizationCompare } from './pages/ModernizationCompare.js';
import { AiSearch } from './pages/AiSearch.js';
import { Report } from './pages/Report.js';
import { ModernizationPackage } from './pages/ModernizationPackage.js';
import { Billing } from './pages/Billing.js';
import { Checkout } from './pages/Checkout.js';
import { InvoicePage } from './pages/InvoicePage.js';
import { AddSite } from './pages/AddSite.js';
import {
  ConfirmRecipient,
  ForgotPassword,
  Login,
  LoginCode,
  Register,
  JoinTeam,
  ResetPassword,
  VerifyEmail,
} from './pages/Auth.js';
import { SiteLayout } from './components/site.js';
import { Findings } from './pages/Findings.js';
import { HelpRequest } from './pages/HelpRequest.js';
import { Incidents } from './pages/Incidents.js';
import { Links } from './pages/Links.js';
import { Notifications } from './pages/Notifications.js';
import { Onboarding } from './pages/Onboarding.js';
import { Overview } from './pages/Overview.js';
import { AccountDeleted, ExportDownload, RestoreAccount } from './pages/Privacy.js';
import { QuickCheckConfirm, QuickCheckResult, QuickCheckStart } from './pages/QuickCheck.js';
import { Requests } from './pages/Requests.js';
import { SharedFixes } from './pages/SharedFixes.js';
import { SiteDashboard } from './pages/SiteDashboard.js';
import { SiteSettings } from './pages/SiteSettings.js';
import { Speed } from './pages/Speed.js';
import { StatusPage } from './pages/StatusPage.js';
import { FrameScope } from './theme.js';

/** A site page over the whole width, the side panel below it (site-findings.html). */
const WIDE = { wide: true };

/** Screens of the app (all on the new design, SP-UX10–UX12). */
const screens = [
  {
    element: <PublicLayout />,
    children: [
      { path: '/login', element: <Login /> },
      { path: '/login/code', element: <LoginCode /> },
      { path: '/register', element: <Register /> },
      { path: '/forgot-password', element: <ForgotPassword /> },
      { path: '/reset-password', element: <ResetPassword /> },
      { path: '/join', element: <JoinTeam /> },
      { path: '/verify-email', element: <VerifyEmail /> },
      { path: '/confirm-recipient', element: <ConfirmRecipient /> },
    ],
  },
  // Public status page of a site (T-70; status.html): its own frame, for the site's visitors.
  { path: '/status/:slug', element: <StatusPage /> },
  // Ready-made fixes sent to a web designer (T-72): the link is the access; its own frame (fix.html).
  { path: '/fix/:token', element: <SharedFixes /> },
  // Public Quick-Check (US-09): no account needed.
  {
    element: <PublicLayout wide />,
    children: [
      { path: '/check', element: <QuickCheckStart /> },
      { path: '/check/:checkId', element: <QuickCheckResult /> },
      { path: '/check/:checkId/confirm', element: <QuickCheckConfirm /> },
      // After deleting the account and from its e-mail: one card in the middle (privacy.html).
      { path: '/restore-account', element: <RestoreAccount /> },
      { path: '/account-deleted', element: <AccountDeleted /> },
    ],
  },
  {
    element: <AppLayout />,
    children: [
      { path: '/', element: <Overview /> },
      { path: '/sites/new', element: <AddSite /> },
      // The first checks of a new site stand on their own, outside the site pages (onboarding.html).
      { path: '/sites/:siteId/onboarding', element: <Onboarding /> },
      {
        path: '/sites/:siteId',
        element: <SiteLayout />,
        children: [
          { index: true, element: <SiteDashboard /> },
          { path: 'area/:area', element: <Area /> },
          { path: 'changes', element: <Changes /> },
          { path: 'modernization', element: <Modernization /> },
          { path: 'modernization/compare', element: <ModernizationCompare /> },
          { path: 'modernization/:packageKey', element: <ModernizationPackage /> },
          { path: 'findings', element: <Findings />, handle: WIDE },
          { path: 'incidents', element: <Incidents />, handle: WIDE },
          { path: 'links', element: <Links />, handle: WIDE },
          { path: 'speed', element: <Speed />, handle: WIDE },
          { path: 'ai-search', element: <AiSearch /> },
          { path: 'report', element: <Report /> },
          { path: 'settings', element: <SiteSettings /> },
          { path: 'help', element: <HelpRequest /> },
        ],
      },
      { path: '/requests', element: <Requests /> },
      { path: '/billing', element: <Billing /> },
      { path: '/billing/checkout', element: <Checkout /> },
      { path: '/billing/invoices/:invoiceId', element: <InvoicePage /> },
      // The operator area is loaded on demand: customers never download it.
      {
        path: '/admin',
        lazy: async () => ({
          Component: (await import('./pages/admin/AdminLayout.js')).AdminLayout,
        }),
        children: [
          {
            index: true,
            lazy: async () => ({
              Component: (await import('./pages/admin/AdminAccounts.js')).AdminAccounts,
            }),
          },
          {
            path: 'accounts/:accountId',
            lazy: async () => ({
              Component: (await import('./pages/admin/AdminAccounts.js')).AdminAccount,
            }),
          },
          {
            path: 'billing',
            lazy: async () => ({
              Component: (await import('./pages/admin/AdminBilling.js')).AdminBilling,
            }),
          },
          {
            path: 'analytics',
            lazy: async () => ({
              Component: (await import('./pages/admin/AdminAnalytics.js')).AdminAnalytics,
            }),
          },
          {
            path: 'invoices/:invoiceId',
            lazy: async () => ({
              Component: (await import('./pages/admin/AdminBilling.js')).AdminInvoice,
            }),
          },
          {
            path: 'invoices/:invoiceId/document',
            lazy: async () => ({
              Component: (await import('./pages/admin/AdminBilling.js')).AdminInvoiceDocument,
            }),
          },
          {
            path: 'requests',
            lazy: async () => ({
              Component: (await import('./pages/admin/AdminRequests.js')).AdminRequests,
            }),
          },
          {
            path: 'requests/:requestId',
            lazy: async () => ({
              Component: (await import('./pages/admin/AdminRequests.js')).AdminRequest,
            }),
          },
          {
            path: 'services',
            lazy: async () => ({
              Component: (await import('./pages/admin/AdminServices.js')).AdminServices,
            }),
          },
          {
            path: 'team',
            lazy: async () => ({
              Component: (await import('./pages/admin/AdminTeam.js')).AdminTeam,
            }),
          },
          {
            path: 'system',
            lazy: async () => ({
              Component: (await import('./pages/admin/AdminSystem.js')).AdminSystem,
            }),
          },
        ],
      },
      { path: '/notifications', element: <Notifications /> },
      { path: '/account', element: <Account /> },
      { path: '/export', element: <ExportDownload /> },
    ],
  },
  { path: '*', element: <NotFound /> },
];

/** The site frame learns whether the screen shown is wide (src/theme.tsx). */
export const routes = [{ element: <FrameScope />, children: screens }];

export const router = createBrowserRouter(routes);
